import { useQuery } from '@apollo/client';
import React, { useMemo } from 'react';
import OverviewCard from '../../components/connectedForm/OverviewCard';
import ValueCard from '../../components/connectedForm/ValueCard';
import { Button } from '../../components/connectedForm/Button';
import styled from 'styled-components';
import H2 from '../../components/connectedForm/Heading2';
import { Image } from '@fluentui/react/lib/Image';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { EdataProductDetailsQuery } from '../../graphql/queries/product/EdataProductDetailsQuery.graphql';
import Form from '../../components/connectedForm';
import { IEdataFeatureDto, IEdataProductDto, IEdataAssetDto } from '../../graphql/schema/p4types';
import { Maybe } from 'graphql/jsutils/Maybe';
import OverviewInlineField from '../../components/connectedForm/OverviewInlineField';
import {
  Carousel,
  CarouselCard,
  CarouselNav,
  CarouselNavButton,
  CarouselNavContainer,
  CarouselSlider,
  CarouselViewport,
} from '@fluentui/react-components';
import { HoldingMessage } from '../../components/FeatureFlag/HoldingMessage';
import { EdataHoldingInfo } from '../../components/FeatureFlag/EdataHoldingInfo';
import { useFullUserDetails } from '../../utils/hooks';

const BasicContainer = styled.div`
  flex-grow: 1;
  flex-basis: auto;
  width: auto;
  max-width: none;
  text-align: left;
  padding: 12px 16px 12px 20px;
`;

const EdataProductDetails = ({
  providedId,
  onAssetAttach,
  onDataSheetAttach,
  enquiryMode,
}: {
  providedId: number;
  onAssetAttach: (asset: IEdataAssetDto) => void;
  onDataSheetAttach: (asset: IEdataProductDto) => void;
  enquiryMode: boolean;
}) => {
  const edataProductId = providedId ? providedId : window.location.href.split('/').pop();

  const { data: edata } = useQuery(EdataProductDetailsQuery, {
    variables: { edataProductId: edataProductId },
  });

  const cards = useMemo(() => {
    return edata?.data?.images?.map((image: string) => {
      return (
        <>
          <CarouselCard>
            <img src={image} alt="" style={{ maxHeight: '300px', paddingLeft: '10%' }} />
          </CarouselCard>
        </>
      );
    });
  }, [edata?.data?.images]);

  const userDetails = useFullUserDetails();

  if (!userDetails?.flags?.edataEnabled) {
    return <HoldingMessage {...EdataHoldingInfo} />;
  }

  if (!edata) {
    return <Spinner size={SpinnerSize.large} />;
  }

  const values: IEdataProductDto = edata.data;

  return (
    <Form.FormContainer>
      <Form.Form>
        <Form.Row>
          <Form.Fieldset cols={6 / 8}>
            <Form.HeaderTitle value={`${values.brand} ${values.manufacturerPartNumber}`} />
            <H2>{values.shortDescription}</H2>
            <BasicContainer>{values.longDescription}</BasicContainer>
          </Form.Fieldset>
          <Form.Fieldset cols={1 / 8}>
            <BasicContainer>
              <Button
                onClick={() => {
                  window.open(`/api/edata/datasheet/${values.gtin}`, '_blank');
                }}
              >
                View Data Sheet
              </Button>
              {enquiryMode && (
                <Button
                  onClick={() => {
                    onDataSheetAttach(values);
                  }}
                >
                  Attach Data Sheet
                </Button>
              )}
            </BasicContainer>
          </Form.Fieldset>
          <Form.Fieldset cols={1 / 8}>
            <Image src={'/images/edata_square.jpg'} style={{ width: '60%', float: 'right', paddingTop: '18px' }} />
          </Form.Fieldset>
        </Form.Row>
        <Form.Row>
          <Form.Fieldset cols={1}>
            <Form.Legend label={'Technical Specs'} />
            <Form.Fieldset cols={1 / 5}>
              <Image src={'/images/etim_logo.png'} style={{ width: '50px', paddingBottom: '15px' }} />
            </Form.Fieldset>
            <Form.Row>
              <Form.Fieldset cols={1}>
                <Form.Row style={{ paddingRight: '10px' }}>
                  {values?.features?.map((feature: Maybe<IEdataFeatureDto>) => {
                    if (!feature || feature?.featureValue === null) {
                      return null;
                    }
                    return (
                      <Form.Fieldset cols={1 / 2}>
                        <OverviewInlineField
                          label={feature.featureName}
                          value={feature.featureValue}
                          alignValue={'left'}
                          type="text"
                          fontSize="13px"
                        />
                      </Form.Fieldset>
                    );
                  })}
                </Form.Row>
              </Form.Fieldset>
            </Form.Row>
            <Form.Legend label={'Assets'} />
            <Form.Row>
              <Form.Fieldset cols={1}>
                <Form.Row style={{ paddingRight: '10px' }}>
                  {values?.assets?.map((asset) => {
                    if (!asset || (asset.assetDescription === null && asset.assetFilename === null)) {
                      return null;
                    }
                    return (
                      <Form.Fieldset cols={1 / 3}>
                        <ValueCard
                          readOnly={true}
                          value={asset?.assetFilename}
                          label={asset?.assetDescription ? asset.assetDescription : asset?.assetFilename}
                          labelPosition={'top'}
                          type={'text'}
                          align={'left'}
                          showtag={true}
                          onClick={() => {
                            window.open(asset?.assetSource || '', '_blank');
                          }}
                          actionIconName={enquiryMode ? 'upload' : undefined}
                          onActionIconClick={() => {
                            onAssetAttach(asset);
                          }}
                        />
                      </Form.Fieldset>
                    );
                  })}
                </Form.Row>
              </Form.Fieldset>
            </Form.Row>
          </Form.Fieldset>
        </Form.Row>
        <Form.Legend label={'General'} />
        <Form.Row>
          <Form.Fieldset cols={2 / 8}>
            <Carousel groupSize={1} circular={true}>
              <CarouselViewport>
                <CarouselSlider>{cards}</CarouselSlider>
              </CarouselViewport>
              <CarouselNavContainer
                layout="inline"
                nextTooltip={{ content: 'Go to next', relationship: 'label' }}
                prevTooltip={{ content: 'Go to prev', relationship: 'label' }}
              >
                <CarouselNav>
                  {(index) => <CarouselNavButton aria-label={`Carousel Nav Button ${index}`} />}
                </CarouselNav>
              </CarouselNavContainer>
            </Carousel>
          </Form.Fieldset>
          <Form.Fieldset cols={6 / 8}>
            <Form.Row>
              <BasicContainer>{values.longDescription}</BasicContainer>
            </Form.Row>
            <Form.Row>
              <ul>
                {values.marketingBullet1 ? <li>{values.marketingBullet1}</li> : null}
                {values.marketingBullet2 ? <li>{values.marketingBullet2}</li> : null}
                {values.marketingBullet3 ? <li>{values.marketingBullet3}</li> : null}
                {values.marketingBullet4 ? <li>{values.marketingBullet4}</li> : null}
                {values.marketingBullet5 ? <li>{values.marketingBullet5}</li> : null}
                {values.marketingBullet6 ? <li>{values.marketingBullet6}</li> : null}
                {values.marketingBullet7 ? <li>{values.marketingBullet7}</li> : null}
                {values.marketingBullet8 ? <li>{values.marketingBullet8}</li> : null}
                {values.marketingBullet9 ? <li>{values.marketingBullet9}</li> : null}
                {values.marketingBullet10 ? <li>{values.marketingBullet10}</li> : null}
              </ul>
            </Form.Row>
          </Form.Fieldset>
        </Form.Row>
        <Form.Row>
          <Form.Fieldset cols={1}>
            <OverviewCard>
              <Form.Row style={{ paddingRight: '10px' }}>
                <Form.Fieldset cols={1 / 5}>
                  <ValueCard
                    readOnly={true}
                    value={values.manufacturerName}
                    label="Manufacturer"
                    labelPosition={'top'}
                    type={'text'}
                    align={'left'}
                  />
                </Form.Fieldset>
                <Form.Fieldset cols={1 / 5}>
                  <ValueCard
                    readOnly={true}
                    value={values.gtin}
                    label="GTIN"
                    labelPosition={'top'}
                    type={'text'}
                    align={'left'}
                  />
                </Form.Fieldset>
                <Form.Fieldset cols={1 / 5}>
                  <ValueCard
                    readOnly={true}
                    value={values.family}
                    label="Family"
                    labelPosition={'top'}
                    type={'text'}
                    align={'left'}
                  />
                </Form.Fieldset>
                <Form.Fieldset cols={1 / 5}>
                  <ValueCard
                    readOnly={true}
                    value={values.range}
                    label="Range"
                    labelPosition={'top'}
                    type={'text'}
                    align={'left'}
                  />
                </Form.Fieldset>
                <Form.Fieldset cols={1 / 5}>
                  <ValueCard
                    readOnly={true}
                    value={'Manufacturers Page'}
                    label="Link"
                    labelPosition={'top'}
                    type={'text'}
                    align={'left'}
                    onClick={() => {
                      window.open(values.deeplink, '_blank');
                    }}
                  />
                </Form.Fieldset>
              </Form.Row>
            </OverviewCard>
          </Form.Fieldset>
        </Form.Row>
      </Form.Form>
    </Form.FormContainer>
  );
};

export default EdataProductDetails;
